import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "../../../components/Select";
import { useToast } from "../../../components/Toast";
import { formatDate } from "../../../lib/format";
import type { ApiError } from "../../../api/client";
import { retainedSampleApi, type RetainedSample } from "../../../api/retainedSamples";
import { traceabilityApi, type LotHit } from "../../../api/traceability";
import { lotsApi, type ReceivingLot } from "../../../api/lots";
import { deliveriesApi, type Delivery } from "../../../api/deliveries";
import { retainedErrorMessage } from "./retainedSampleErrors";

/** Na čemu uzorak visi. Baza dopušta tačno jednu vezu, pa je i ekran bira kao jednu. */
type SubjectKind = "DELIVERY" | "RECEIVING_LOT" | "GOODS_LOT";

/**
 * Unos kontrauzorka.
 *
 * Rok se ne računa ovde. Server zna šest meseci iz KK32 za sirovinu i zna da za gotov proizvod rok nije
 * odgovoren (KK52), pa polje ostaje prazno i on upisuje ono što važi. Da ekran sam računa isti datum,
 * postojala bi dva računa istog pravila — a kad se razlikuju, razlikuje se i dan kad neko baci teglu.
 */
export default function RetainedSampleForm({
  onSaved,
  onCancel,
}: {
  onSaved: (s: RetainedSample) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const lang = i18n.resolvedLanguage ?? "sr";

  const [subject, setSubject] = useState<SubjectKind>("DELIVERY");
  const [label, setLabel] = useState("");
  const [takenOn, setTakenOn] = useState(new Date().toISOString().slice(0, 10));
  const [keepUntil, setKeepUntil] = useState("");
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [deliveryId, setDeliveryId] = useState("");
  const [receivingLots, setReceivingLots] = useState<ReceivingLot[]>([]);
  const [receivingLotId, setReceivingLotId] = useState("");
  const [lotTerm, setLotTerm] = useState("");
  const [lotHits, setLotHits] = useState<LotHit[] | null>(null);
  const [goodsLot, setGoodsLot] = useState<LotHit | null>(null);

  useEffect(() => {
    if (subject === "DELIVERY" && deliveries.length === 0) {
      // Birač isporuke uz kontrauzorak: jedna strana najnovijih, ne ceo registar.
      void deliveriesApi
        .list({}, 0, 200)
        .then((r) => setDeliveries(r.content))
        .catch(() => setDeliveries([]));
    }
    if (subject === "RECEIVING_LOT" && receivingLots.length === 0) {
      void lotsApi
        .list()
        .then(setReceivingLots)
        .catch(() => setReceivingLots([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject]);

  function pickedSubject(): { field: string; id: string } | null {
    if (subject === "DELIVERY") return deliveryId ? { field: "deliveryId", id: deliveryId } : null;
    if (subject === "RECEIVING_LOT")
      return receivingLotId ? { field: "receivingLotId", id: receivingLotId } : null;
    return goodsLot ? { field: "goodsLotId", id: goodsLot.id } : null;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const veza = pickedSubject();
    if (!veza) {
      toast.error(t("retained.pickSubject", "Izaberite sa čega je uzorak uzet."));
      return;
    }
    if (!label.trim()) {
      toast.error(t("retained.labelRequired", "Upišite oznaku sa tegle."));
      return;
    }
    setSaving(true);
    try {
      const saved = await retainedSampleApi.create({
        label: label.trim(),
        takenOn,
        keepUntil: keepUntil || null,
        location: location.trim() || null,
        note: note.trim() || null,
        [veza.field]: veza.id,
      });
      toast.success(t("retained.saved", "Uzorak je upisan."));
      onSaved(saved);
    } catch (err) {
      // Poznata oznaka greške se saopštava na srpskom; nepoznata ostaje kako ju je server objasnio.
      const prevod = retainedErrorMessage(err as ApiError);
      toast.error(prevod ? t(prevod[0], prevod[1]) : (err as ApiError).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="card fadein" onSubmit={submit}>
      <div className="card-h">
        <strong>{t("retained.newTitle", "Nov kontrauzorak")}</strong>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span className="muted">{t("retained.subjectLabel", "Uzorak je uzet sa")}</span>
          <div className="seg" role="group">
            {(
              [
                ["DELIVERY", t("retained.subject.DELIVERY", "isporuke")],
                ["RECEIVING_LOT", t("retained.subject.RECEIVING_LOT", "prijemne šarže")],
                ["GOODS_LOT", t("retained.subject.GOODS_LOT", "LOT-a gotove robe")],
              ] as [SubjectKind, string][]
            ).map(([k, naziv]) => (
              <button key={k} type="button" aria-pressed={subject === k} onClick={() => setSubject(k)}>
                {naziv}
              </button>
            ))}
          </div>

          {subject === "DELIVERY" && (
            <Select
              value={deliveryId}
              onChange={setDeliveryId}
              placeholder={t("retained.pickDelivery", "izaberite isporuku")}
              options={deliveries.map((d) => ({
                value: d.id,
                label: `${formatDate(d.announcedDate, lang)} — ${d.companyName}${d.vehiclePlate ? ` — ${d.vehiclePlate}` : ""}`,
              }))}
            />
          )}

          {subject === "RECEIVING_LOT" && (
            <Select
              value={receivingLotId}
              onChange={setReceivingLotId}
              placeholder={t("retained.pickReceivingLot", "izaberite šaržu")}
              options={receivingLots.map((l) => ({
                value: l.id,
                label: `${l.lotNumber} — ${l.culture} — ${formatDate(l.receivedOn, lang)}`,
              }))}
            />
          )}

          {subject === "GOODS_LOT" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div className="toolbar">
                <input
                  className="input"
                  value={lotTerm}
                  onChange={(e) => setLotTerm(e.target.value)}
                  placeholder={t("retained.lotSearch", "oznaka LOT-a")}
                />
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    void traceabilityApi
                      .search(lotTerm)
                      .then((r) => setLotHits(r.rows))
                      .catch(() => setLotHits([]));
                  }}
                >
                  {t("common.search", "Traži")}
                </button>
              </div>
              {goodsLot ? (
                <span className="tag neutral">
                  {goodsLot.designation ?? goodsLot.id} — {goodsLot.productName ?? ""}
                </span>
              ) : (
                lotHits !== null && (
                  <div className="cat-list">
                    {lotHits.length === 0 && (
                      <div className="empty">{t("retained.noLot", "Nema LOT-a po toj oznaci.")}</div>
                    )}
                    {lotHits.slice(0, 20).map((h) => (
                      <button key={h.id} type="button" className="btn ghost sm" onClick={() => setGoodsLot(h)}>
                        {h.designation ?? h.id} — {h.productName ?? ""}
                      </button>
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
          <label>
            <span className="muted">{t("retained.label", "Oznaka sa tegle")}</span>
            <input
              className="input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t("retained.labelHint", "kako je tegla obeležena")}
            />
          </label>
          <label>
            <span className="muted">{t("retained.takenOn", "Uzet")}</span>
            <input
              className="input"
              type="date"
              value={takenOn}
              onChange={(e) => setTakenOn(e.target.value)}
            />
          </label>
          <label>
            <span className="muted">{t("retained.keepUntil", "Drži se do")}</span>
            <input
              className="input"
              type="date"
              value={keepUntil}
              onChange={(e) => setKeepUntil(e.target.value)}
            />
          </label>
          <label>
            <span className="muted">{t("retained.location", "Mesto")}</span>
            <input
              className="input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t("retained.locationHint", "polica, red")}
            />
          </label>
        </div>

        <div className="muted">
          {subject === "GOODS_LOT"
            ? t(
                "retained.hintGoodsLot",
                "Za gotov proizvod rok čuvanja nije dat, pa ostaje prazan dok se ne upiše. Prazan rok znači da uzorak nema dan za bacanje, ne da je istekao.",
              )
            : t(
                "retained.hintRawMaterial",
                "Ostavljen prazan rok znači šest meseci od dana uzimanja, kako se sirovina i drži. Drugi dan se sme upisati.",
              )}
        </div>

        <label>
          <span className="muted">{t("retained.note", "Napomena")}</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        <div className="page-actions">
          <button type="submit" className="btn primary" disabled={saving}>
            {t("retained.save", "Upiši uzorak")}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={saving}>
            {t("common.cancel", "Otkaži")}
          </button>
        </div>
      </div>
    </form>
  );
}
